// Build-time inclusion of the Obsidian vault into the app bundle.
//
// Vite's import.meta.glob reads every .md under /docs/ at build time and
// gives us `{ path: content }`. No runtime fetch, no separate CMS —
// editing a .md, committing, and pushing rebuilds the SWA and ships the
// new docs. That's the whole point.

const rawFiles = import.meta.glob("/docs/**/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

export interface DocEntry {
  /** Path under docs/ — e.g. "PROCESS.md" or "spec/modules/vacancies.md" */
  path: string;
  /** Raw markdown content */
  content: string;
  /** First `# heading` on the first line, if present, else the filename */
  title: string;
}

function extractTitle(content: string, fallback: string): string {
  const firstLine = content.split("\n").find((l) => l.trim().length > 0) ?? "";
  const match = firstLine.match(/^#\s+(.+?)\s*$/);
  return match ? match[1]! : fallback;
}

function makePath(gloKey: string): string {
  // Vite gives us paths like "/docs/PROCESS.md" — strip the prefix.
  return gloKey.replace(/^\/docs\//, "");
}

export const DOCS: DocEntry[] = Object.entries(rawFiles)
  .map(([key, content]) => {
    const path = makePath(key);
    const filename = path.split("/").pop() ?? path;
    return {
      path,
      content,
      title: extractTitle(content, filename.replace(/\.md$/, "")),
    };
  })
  .sort((a, b) => a.path.localeCompare(b.path));

const byPath = new Map(DOCS.map((d) => [d.path, d]));

export function getDoc(path: string): DocEntry | null {
  return byPath.get(path) ?? null;
}

export function findDoc(needle: string): DocEntry | null {
  // Best-effort resolver used by wiki-link handling.
  // "[[PROCESS]]" → PROCESS.md
  // "[[BRIEFING]]" → BRIEFING.md
  // "[[architecture/build-status]]" → architecture/build-status.md
  // "[[spec/modules]]" → spec/modules.md
  const normalized = needle.trim().replace(/\.md$/, "");
  const withExt = `${normalized}.md`;
  return (
    byPath.get(withExt) ??
    byPath.get(normalized) ??
    // Try suffix match — allows [[Vacancies]] to hit spec/modules/vacancies.md
    DOCS.find(
      (d) =>
        d.path.toLowerCase().endsWith(`/${withExt.toLowerCase()}`) ||
        d.path.toLowerCase() === withExt.toLowerCase(),
    ) ??
    null
  );
}
